/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo, useEffect, useState, useCallback } from 'react'
import Link from 'next/link'
import { NextPage, NextPageContext } from 'next'
import styled from 'styled-components'
import { handingServerError, getHost } from 'fbk-utils'
import { Section } from 'fbk-components'
import { Button, Footer, Header } from 'components'
import Error from 'pages/_error'
import { HOME } from 'constants/routes'
import * as api from 'api'
import { SiteContentProvider } from 'contexts'
import { SiteContent } from 'types'

type PageProps =
  | {
      uuid: string
      number: string
      host: string
      siteContent: SiteContent
      error: null
    }
  | {
      error: {
        code: number
        message: string
      }
    }

const Wrapper = styled(Section)`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  margin-top: 60px;

  .title {
    margin-bottom: 24px;
    font-size: 30px;
    line-height: 34px;
  }

  @media (min-width: 768px) {
    margin-top: 80px;

    .title {
      font-size: 44px;
      line-height: 48px;
    }

    .btn-unsubscribe {
      width: 300px;
    }

    .btn-link {
      width: 240px;
    }
  }
`

const News: NextPage<PageProps> = props => {
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (props.error) {
    return <Error error={props.error} />
  }

  const { number, uuid, siteContent, host } = props

  const localStorageKey = `unsubscribed-${number}`

  const [unsubscribeConfirmed, setUnsubscribe] = useState(false)

  useEffect(() => {
    const unsubscribeWasConfirmed =
      localStorage.getItem(localStorageKey) === 'true'

    if (unsubscribeWasConfirmed) {
      setUnsubscribe(true)
    }
  }, [localStorageKey, number])

  const handleUnsubscribe = useCallback(async () => {
    try {
      setError('')
      setLoading(true)

      await api.unsubscribe(uuid, 'POST')

      setUnsubscribe(true)
      localStorage.setItem(localStorageKey, `true`)
    } catch (err) {
      console.error(err)
      if (err.error && err.error.detail) {
        setError(err.error.detail)
      }
    } finally {
      setLoading(false)
    }
  }, [localStorageKey, uuid])

  const content = useMemo(() => {
    if (unsubscribeConfirmed)
      return (
        <>
          <h1 className="title">
            Подписка на обновления по делу {number}{' '}
            <span className="g-red-text">отменена</span>
          </h1>
          <Link href="/">
            <Button isLink isLight className="btn-link">
              Перейти на сайт
            </Button>
          </Link>
        </>
      )

    return (
      <>
        <h1 className="title">
          Вы хотите{' '}
          <span className="g-red-text">отменить подписку на обновления</span>{' '}
          по делу {number}?
        </h1>
        <Button
          color="accent"
          onClick={handleUnsubscribe}
          isLoader={isLoading}
          loaderText="Отменяем"
          className="btn-unsubscribe"
        >
          Да, отменить подписку
        </Button>
        {error && <p className="g-red-text">{error}</p>}
      </>
    )
  }, [error, handleUnsubscribe, isLoading, number, unsubscribeConfirmed])

  const title = 'Отмена подписки'

  const goBackProps = {
    title: 'Наглавную',
    ...HOME,
  }

  return (
    <SiteContentProvider data={siteContent} host={host}>
      <title>Отмена подписки</title>
      <Header
        title={title}
        breadcrumbs={[{ title: 'Главная', ...HOME }]}
        goBackProps={goBackProps}
      />
      <Wrapper space="l">{content}</Wrapper>
      <Footer />
    </SiteContentProvider>
  )
}

export const getServerSideProps = async (ctx: NextPageContext) => {
  try {
    const { uuid } = ctx.query

    if (typeof uuid !== 'string')
      return {
        props: {
          error: {
            code: 404,
            message: 'Такой страницы не существует',
          },
        },
      }

    const { number } = await api.unsubscribe(uuid, 'GET')

    return {
      props: {
        uuid,
        host: getHost(ctx),
        number,
        error: null,
      },
    }
  } catch (err) {
    const error = handingServerError(ctx, err)

    return {
      props: {
        error,
      },
    }
  }
}

export default News
